// Thin fetch wrapper that talks to the api-gateway. Three things to
// know about our gateway-vs-frontend contract:
//
//   1. Cookie auth — the gateway sets `sm_session=<opaque>` HttpOnly
//      cookie on POST /api/auth/login. The browser ships it
//      automatically as long as we use `credentials: "include"`.
//
//   2. CSRF — every state-changing request must carry the session's
//      `csrf_token` in `X-CSRF-Token`. Login returns the token; we
//      stash it in localStorage so it survives reload, and replay
//      it on subsequent POST/PATCH/DELETE.
//
//   3. Error shape — the gateway's middleware.WriteError returns
//      `{ error: { code, message } }` on non-2xx. We unbox into a
//      typed APIError so React Query consumers can pattern-match.

const CSRF_KEY = "sm_csrf";

export class APIError extends Error {
  status: number;
  code: string;
  body: unknown;
  constructor(status: number, code: string, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function getCSRF(): string | null {
  return typeof window !== "undefined" ? window.localStorage.getItem(CSRF_KEY) : null;
}

export function setCSRF(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(CSRF_KEY, token);
  else window.localStorage.removeItem(CSRF_KEY);
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  // signal lets TanStack Query cancel inflight requests on unmount.
  signal?: AbortSignal;
  // skipCSRF defaults to false; set true on login/register where we
  // don't have a session yet.
  skipCSRF?: boolean;
  // expectMFAChallenge surfaces the gateway's 428 Precondition Required
  // response (RFC 6585) as a returned object instead of throwing —
  // the login flow uses this to branch into the TOTP prompt step
  // when the password was correct but MFA is enrolled on the account.
  expectMFAChallenge?: boolean;
  // Extra request headers — merged on top of the defaults. Used by
  // the redeem mutation to forward the Idempotency-Key.
  headers?: Record<string, string>;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  // FormData (avatar upload) must keep the browser-generated multipart
  // Content-Type, boundary and all — so only JSON bodies get an
  // explicit application/json header.
  const isFormData =
    typeof FormData !== "undefined" && opts.body instanceof FormData;
  if (opts.body !== undefined && !isFormData)
    headers["Content-Type"] = "application/json";

  if (!opts.skipCSRF && method !== "GET") {
    const tok = getCSRF();
    if (tok) headers["X-CSRF-Token"] = tok;
  }
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body:
      opts.body === undefined
        ? undefined
        : isFormData
          ? (opts.body as FormData)
          : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  // Empty 204s round-trip as undefined.
  if (res.status === 204) return undefined as T;

  let json: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }
  }

  if (res.status === 428 && opts.expectMFAChallenge) {
    return json as T;
  }

  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } })?.error;
    throw new APIError(
      res.status,
      err?.code ?? "UNKNOWN",
      err?.message ?? `HTTP ${res.status}`,
      json,
    );
  }
  return json as T;
}

// Query-string builder used by the typed wrappers below — accepts
// a plain object and skips undefined / "" values. Array values emit a
// repeated param (?k=a&k=b), the shape the gateway parses for multi-
// valued filters like ?market_id; empty arrays/elements are skipped.
export function qs(
  params: Record<string, string | number | string[] | undefined>,
): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null || item === "") continue;
        usp.append(k, String(item));
      }
      continue;
    }
    if (v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}
